import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  login,
  AuthType,
  type LoginByPhonePwdRequest,
  type LoginByAccountRequest,
  type LoginResponse,
  type ApiResponse,
} from '@/api'
import { GlobalCode, StatusCode } from '@/enums/api'
import router from '@/router'
import { getDevice, toastify } from '@/utils'
import { useChatStore } from './chat'
import { UserRole } from '@/enums'
import dayjs from 'dayjs'

/**
 * 构建登录失败提示文案
 * @param error 后端错误对象
 * @param wrongPwdHint 前 3 次错误的文案（手机登录和账号登录文案不同）
 */
function buildLoginErrorMessage(error: ApiResponse<LoginResponse>, wrongPwdHint: string): string {
  if (error.code === StatusCode.WrongPassword && error.data?.attempts) {
    const { left_attempts, max_attempts } = error.data.attempts as {
      left_attempts: number
      max_attempts: number
    }
    const used = max_attempts - left_attempts
    if (used < 3) return wrongPwdHint
    return `密码错误次数过多，您还剩余${left_attempts}次机会`
  }
  if (error.code === StatusCode.LoginFrozen && error.data?.attempts) {
    const unlock_at: number = error.data.attempts.lock_left_secs
    const hours = Math.floor(unlock_at / 3600)
    const minutes = Math.ceil((unlock_at % 3600) / 60)
    if (minutes == 60) {
      return `账户已冻结，剩余 ${hours + 1} 小时`
    }
    const timeStr = `${hours}小时${minutes}分`
    return `账户已冻结，剩余 ${timeStr}`
  }
  return error.message || ''
}

/**
 * 认证 Store
 * 管理登录状态、用户信息和记住密码功能
 */
export const useAuthStore = defineStore(
  'auth',
  () => {
    const isAuthenticated = ref(false)
    const token = ref<string | null>(null)
    const userInfo = ref<any>(null)

    /** 是否为商户会员 */
    const isMerMember = computed(() => userInfo.value?.role == UserRole.MerchantMember)
    const mobileRemember = ref(false)
    const savedMobilePhone = ref('')
    const savedMobilePassword = ref('')

    const accountRemember = ref(false)
    const savedAccountUsername = ref('')
    const savedAccountPassword = ref('')

    async function afterLoginSuccess() {
      isAuthenticated.value = true
      const chatStore = useChatStore()
      await chatStore.connectWebSocket()
    }

    async function loginByPhone(
      values: LoginByPhonePwdRequest,
      remember: boolean,
      onForceCaptcha?: (captchaNo: string) => void,
    ): Promise<void> {
      try {
        const res = await login({
          area_code: '86',
          phone: values.phone,
          password: values.password,
          type: AuthType.PhonePwd,
          device_name: getDevice(),
          captcha_no: values.captcha_no,
          captcha_param: values.captcha_param,
        })

        if (res.data?.force_captcha) {
          onForceCaptcha?.(res.data.captcha_no ?? '')
          return
        }

        if (res.code == GlobalCode.Success) {
          isAuthenticated.value = true
          const chatStore = useChatStore()
          await chatStore.connectWebSocket()
        }

        if (remember) {
          mobileRemember.value = true
          savedMobilePhone.value = values.phone
          savedMobilePassword.value = values.password
        } else {
          mobileRemember.value = false
          savedMobilePhone.value = ''
          savedMobilePassword.value = ''
        }

        const redirect = router.currentRoute.value.query.redirect
        const target =
          typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
            ? redirect
            : '/'
        setTimeout(() => router.replace(target), 0)
      } catch (error: any) {
        const message = buildLoginErrorMessage(error, '手机号或密码错误，请重新输入')
        toastify({ type: 'danger', message })
        throw error
      }
    }

    async function loginByAccount(
      values: LoginByAccountRequest,
      remember: boolean,
      onForceCaptcha?: (captchaNo: string) => void,
    ): Promise<void> {
      try {
        const res = await login({
          type: AuthType.AccountPassword,
          account: values.account.trim(),
          password: values.password,
          device_name: getDevice(),
          captcha_no: values.captcha_no,
          captcha_param: values.captcha_param,
        })
        console.log(
          '登录结果',
          { ...res.data },
          'force_captcha:',
          res.data?.force_captcha,
          onForceCaptcha,
        )
        if (res.data?.force_captcha) {
          onForceCaptcha?.(res.data.captcha_no ?? '')
          return
        }

        if (res.code == GlobalCode.Success) {
          isAuthenticated.value = true
          const chatStore = useChatStore()
          chatStore.connectWebSocket()
        }

        if (remember) {
          accountRemember.value = true
          savedAccountUsername.value = values.account.trim()
          savedAccountPassword.value = values.password
        } else {
          accountRemember.value = false
          savedAccountUsername.value = ''
          savedAccountPassword.value = ''
        }

        const redirect = router.currentRoute.value.query.redirect
        const target =
          typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
            ? redirect
            : '/'

        setTimeout(() => router.replace(target), 0)
      } catch (e: unknown) {
        const error = e as any
        const message = buildLoginErrorMessage(error, '账号或密码错误，请重新输入')
        toastify({ type: 'danger', message })
        throw e
      }
    }

    function logout() {
      isAuthenticated.value = false
      token.value = null
      userInfo.value = null
    }

    function clearMobileRemember() {
      mobileRemember.value = false
      savedMobilePhone.value = ''
      savedMobilePassword.value = ''
    }

    function clearAccountRemember() {
      accountRemember.value = false
      savedAccountUsername.value = ''
      savedAccountPassword.value = ''
    }

    function getMobileCredentials() {
      if (mobileRemember.value && savedMobilePhone.value && savedMobilePassword.value) {
        return {
          phone: savedMobilePhone.value,
          password: savedMobilePassword.value,
          remember: true,
        }
      }
      return null
    }

    function getAccountCredentials() {
      if (accountRemember.value && savedAccountUsername.value && savedAccountPassword.value) {
        return {
          account: savedAccountUsername.value,
          password: savedAccountPassword.value,
          remember: true,
        }
      }
      return null
    }

    return {
      isAuthenticated,
      token,
      userInfo,
      isMerMember,
      mobileRemember,
      savedMobilePhone,
      savedMobilePassword,
      accountRemember,
      savedAccountUsername,
      savedAccountPassword,
      loginByPhone,
      loginByAccount,
      logout,
      clearMobileRemember,
      clearAccountRemember,
      getMobileCredentials,
      getAccountCredentials,
      afterLoginSuccess,
    }
  },
  {
    persist: {
      storage: localStorage,
      pick: [
        'mobileRemember',
        'savedMobilePhone',
        'savedMobilePassword',
        'accountRemember',
        'savedAccountUsername',
        'savedAccountPassword',
        'isAuthenticated',
        'token',
        'userInfo',
      ],
    },
  },
)
